import { Button, Card, Field, Input } from "@etain/ui";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLogin, useSession } from "../session.js";
import { MicrosoftLoginFeedback, MicrosoftSignIn } from "../components/MicrosoftSignIn.js";
import { ReturnAfterLogin } from "../components/ReturnAfterLogin.js";
import { loginReturnData } from "../lib/auth-return-target.js";
import { useRecoveryAvailable } from "../lib/auth-recovery.js";

export function LoginPage() {
  const { data: session, isLoading } = useSession();
  const login = useLogin();
  const recoveryAvailable = useRecoveryAvailable();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const destination = loginReturnData(location);
  const microsoftFeedback = new URLSearchParams(location.search).get("sso");
  const hasMicrosoftFeedback =
    microsoftFeedback !== null &&
    ["disabled", "unlinked", "tenant", "failed"].includes(microsoftFeedback);
  if (!isLoading && session && (!hasMicrosoftFeedback || login.isSuccess)) {
    return (
      <ReturnAfterLogin
        key={`${session.user.id}:${destination.target}:${destination.tenantId}`}
        {...destination}
      />
    );
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!email.trim() || !password) return;
    login.reset();
    await login.mutateAsync({ email: email.trim(), password }).catch(() => undefined);
  }

  return (
    <>
      <a className="skip-link" href="#login-content">
        Zum Anmeldeformular
      </a>
      <main id="login-content" className="login-page" tabIndex={-1}>
        <section className="login-brand" aria-label="ETAIN Hub">
          <div className="login-brand__inner">
            <div className="login-brand__logo">
              <img src="/brand/etain-logo.svg" alt="ETAIN Gesundheit" />
              <span>Hub</span>
            </div>
          </div>
        </section>

        <section className="login-form-wrap" aria-labelledby="login-title">
          <Card className="login-card">
            <div className="login-card__mobile-logo">
              <img src="/brand/etain-logo.svg" alt="ETAIN" />
              <span>Hub</span>
            </div>
            <h1 id="login-title">Anmelden</h1>
            <MicrosoftLoginFeedback />
            <MicrosoftSignIn
              destination={destination.target}
              targetTenantId={
                destination.tenantId ??
                new URLSearchParams(location.search).get("mandant") ??
                undefined
              }
            />

            {login.isError ? (
              <div className="form-alert" role="alert">
                <strong>Anmeldung nicht möglich.</strong>
                <span>
                  {login.error instanceof Error
                    ? login.error.message
                    : "Bitte prüfen Sie Ihre Angaben."}
                </span>
              </div>
            ) : null}

            <form className="login-form" onSubmit={handleSubmit}>
              <Field label="E-Mail-Adresse" htmlFor="email" required>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  inputMode="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </Field>
              <Field label="Passwort" htmlFor="password" required>
                <div className="password-input">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((current) => !current)}
                    aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                  >
                    {showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                  </button>
                </div>
              </Field>
              <Button type="submit" busy={login.isPending} className="login-submit">
                Anmelden <ArrowRight size={18} aria-hidden="true" />
              </Button>
            </form>
            <p>
              {recoveryAvailable ? <Link to="/passwort-vergessen">Passwort vergessen?</Link> : null}
            </p>
          </Card>
        </section>
      </main>
    </>
  );
}
